import type { ReactNode } from "react";

import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";

import { getLatestFileMetas7 } from "@synthesis/shared-services/filesystem-gge/documents/client";
import { Modal } from "@synthesis/shared-ui";
import { formatBytes, logger } from "@synthesis/shared-utils";

import { useIfcNames } from "../../files/hooks/use-ifc-names";
import { useUserInformation } from "../../meta-data/hooks/use-user-information";
import type { DrawingCollectionRow } from "../drawings-collection.lib";

type ModalDrawingInfoProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
  drawing?: DrawingCollectionRow;
};

const InfoRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="paragraph-base text-type-common flex items-center gap-1">
    <span className="flex shrink-0 items-center">{label}:</span>
    <span className="flex h-full min-w-0 items-center break-words">{children}</span>
  </div>
);

const ModalDrawingInfoContent = observer(({ drawing, isOpen }: { drawing?: DrawingCollectionRow; isOpen: boolean }) => {
  const [_isAuthorLoading, author] = useUserInformation(drawing?.expert);
  const modelIds = drawing?.ifcBinding ?? [];
  const { ifcNamesById } = useIfcNames(isOpen ? modelIds : []);

  const pdfId = drawing?.pdfId;
  const { data: pdfMeta } = useQuery({
    queryKey: ["filesystem", "drawing-info-pdf-meta", pdfId],
    queryFn: async ({ signal }) => {
      if (!pdfId) return null;
      try {
        const metas = (await getLatestFileMetas7({ body: { ids: [pdfId] }, signal })).data;
        return metas[0] ?? null;
      } catch (error) {
        logger.error("Error fetching drawing PDF meta:", error);
        return null;
      }
    },
    enabled: isOpen && !!pdfId,
    staleTime: 10 * 60 * 1000,
  });

  const authorName = [author?.lastName, author?.firstName, author?.middleName].filter(Boolean).join(" ");
  const linkedModels = modelIds.map((id) => {
    const name = ifcNamesById[id];
    if (name === undefined) return null;
    if (!name) return id;
    return name.toLowerCase().endsWith(".ifc") ? name : `${name}.ifc`;
  });

  return (
    <Modal.Content className="w-[min(480px,95vw)]">
      <Modal.Title>Информация</Modal.Title>
      <div className="flex flex-col gap-2 px-6 pb-6 pt-2">
        <InfoRow label="Наименование">{drawing?.fileName || "—"}</InfoRow>
        <InfoRow label="Тип">PDF</InfoRow>
        <InfoRow label="Размер">{pdfMeta?.size != null ? formatBytes(pdfMeta.size) : "—"}</InfoRow>
        <InfoRow label="Дата загрузки">{drawing?.date || "—"}</InfoRow>
        <InfoRow label="Время загрузки">{drawing?.time || "—"}</InfoRow>
        <InfoRow label="Автор">{authorName || "—"}</InfoRow>
        <div className="paragraph-base text-type-common flex flex-col gap-1">
          <span className="text-type-common">Связанные файлы моделей:</span>
          {modelIds.length === 0 ? (
            <span>—</span>
          ) : (
            <ul className="flex flex-col gap-0.5 pl-0">
              {linkedModels.map((label, index) => (
                <li key={modelIds[index]}>{label ?? <span className="bg-grey-300 inline-block h-4 w-40 animate-pulse rounded" />}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal.Content>
  );
});

export const ModalDrawingInfo = ({ drawing, ...props }: ModalDrawingInfoProps) => (
  <Modal.Root manual {...props}>
    <ModalDrawingInfoContent drawing={drawing} isOpen={props.isOpen} />
  </Modal.Root>
);
